import type { DateTime } from "luxon";
import type { Department } from "../../../../contexts/AuthProvider";
import { slotWindowOnDay } from "../../../../utils/dateUtils";
import { getRoleGroups } from "../../../../utils/roles";
import { formatCount } from "../../WeeklyGapView/CountBadge";
import type { GapsShiftGroup } from "../gapsDayModel";
import { isShownAsShort, shortfallOf } from "../gapsDayModel";
import type { GapsScopedShiftGroup } from "../gapsGroupSheets";

/** The fewest people a post can ask for, and the most — the classic post screen's own 1–100 range. */
export const POST_QUANTITY_MIN = 1;
export const POST_QUANTITY_MAX = 100;

/** A shift window as the time picker holds it. */
export type GapsPostTimes = { startTime: DateTime; endTime: DateTime };

/**
 * What the post sheet opens with when it was started from a group (its `+`, the cover prompt, a short-shift chip).
 * Everything here is the group's own: nothing is guessed, and anything the group cannot say is left for the user.
 */
export type GapsPostPrefill = {
	/** The section's job, for a department that posts by job. `null` for a role or role-group section. */
	jobId: string | null;
	/** The role groups to preselect, for a department that posts by role group. */
	roleGroups: string[];
	neededNurses: number;
	/** The group's own window on this staffing day. */
	times: GapsPostTimes;
};

/**
 * How many people the post asks for, prefilled from the group: its shortfall as the home prints it, rounded UP to a
 * whole person (half a window uncovered still needs somebody), never below one.
 *
 * Deliberately the plain shortfall, not netted against what is already posted: the cover prompt prints the two side
 * by side for the same reason (a posted request is an ask, not a person), and a post that overlaps an open one is
 * caught by the overlap check, which offers to merge instead of posting twice.
 */
export const postQuantityFor = (group: Pick<GapsShiftGroup, "actual" | "required">): number => {
	const shortAsPrinted = Math.ceil(Number(formatCount(shortfallOf(group))));
	// DEFAULT: a group that is not short still opens on one person — the post sheet was asked for, so there is
	// something to post, and one is the least a post can ask for.
	return Math.min(POST_QUANTITY_MAX, Math.max(POST_QUANTITY_MIN, shortAsPrinted));
};

/**
 * The role groups to preselect for a role or role-group section.
 *
 * A section named after a role group is that group. Any other section (a single role) preselects the groups made
 * ENTIRELY of the section's roles (`getRoleGroups`), so the post never asks for a role the section does not hold —
 * a group that merely contains the role would widen an RN section into an RN + LPN post. Job sections return none:
 * they post by job.
 */
export const roleGroupsForSection = (group: Pick<GapsScopedShiftGroup, "jobLabel" | "scope">, department: Department): string[] => {
	if (group.scope.jobId !== null) return [];
	if (department.role_groups?.[group.jobLabel] !== undefined) return [group.jobLabel];
	return getRoleGroups(group.scope.roles, department);
};

/** The whole prefill for a group, on the day the home is showing. */
export const postPrefillFor = (group: GapsScopedShiftGroup, date: DateTime, department: Department): GapsPostPrefill => ({
	jobId: group.scope.jobId,
	roleGroups: roleGroupsForSection(group, department),
	neededNurses: postQuantityFor(group),
	// The group's window as the desktop weekly gap view locks its add dialog to it.
	times: slotWindowOnDay(group, date, department.dayStart),
});

/** Whether a group belongs on a blank post's "prefill from" row: short on its printed value. */
export const isPrefillCandidate = (group: Pick<GapsShiftGroup, "actual" | "required">): boolean => isShownAsShort(shortfallOf(group));
